/**
 * Per-act rider sheets. Stored as one tour-level calendar tab:
 * { v:1, areas: { [areaId]: { categories: [{ id, label, lines: [string] }] } } }
 */

export const RIDER_VEHICLE_AREA_ID = 'vehicle-parking';

function newId(prefix) {
  return `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
}

function cleanCategory(row) {
  if (!row || typeof row !== 'object') return null;
  const label = String(row.label || '').trim();
  if (!label) return null;
  const lines = (Array.isArray(row.lines) ? row.lines : [])
    .map((line) => String(line ?? ''));
  return {
    id: String(row.id || '').trim() || newId('cat'),
    label,
    lines: lines.length ? lines : [''],
  };
}

export function defaultRiderCategories(areaId) {
  if (areaId === RIDER_VEHICLE_AREA_ID) {
    return [
      { id: 'vehicle', label: 'Vehicle Information', lines: [''] },
      { id: 'parking', label: 'Parking', lines: [''] },
    ];
  }
  return [
    { id: 'tech', label: 'Technical', lines: [''] },
    { id: 'hosp', label: 'Hospitality', lines: [''] },
    { id: 'prod', label: 'Production', lines: [''] },
  ];
}

export function parseRiderAreas(raw) {
  const text = String(raw || '').trim();
  if (!text) return {};
  try {
    const parsed = JSON.parse(text);
    const areas = parsed && parsed.v === 1 ? parsed.areas : null;
    if (!areas || typeof areas !== 'object') return {};
    const next = {};
    Object.keys(areas).forEach((id) => {
      const categories = (Array.isArray(areas[id]?.categories) ? areas[id].categories : [])
        .map(cleanCategory)
        .filter(Boolean);
      if (categories.length) next[id] = { categories };
    });
    return next;
  } catch {
    return {};
  }
}

export function categoriesForArea(areas, areaId) {
  const existing = areas?.[areaId]?.categories;
  if (Array.isArray(existing) && existing.length) return existing;
  return defaultRiderCategories(areaId);
}

export function serializeRiderAreas(areas) {
  return JSON.stringify({ v: 1, areas: areas || {} });
}

export function withAreaCategories(areas, areaId, categories) {
  return {
    ...(areas || {}),
    [areaId]: { categories: (categories || []).map(cleanCategory).filter(Boolean) },
  };
}

/** Append a blank line. Empty lines are kept so the new row stays on screen. */
export function addLineToCategory(categories, categoryId) {
  return (categories || []).map((row) => {
    if (!row || row.id !== categoryId) return row;
    const lines = Array.isArray(row.lines) ? row.lines.map((line) => String(line ?? '')) : [''];
    return { ...row, lines: [...lines, ''] };
  });
}
